import * as React from "react"
import { cn } from "@/primitives/utils"

function Table({ className, ...props }: React.ComponentProps<"table">) {
  return (
    <div data-slot="table-container" className="relative w-full overflow-x-auto">
      <table
        data-slot="table"
        // beacon: UI text.
        className={cn("w-full caption-bottom text-ui", className)}
        {...props}
      />
    </div>
  )
}

function TableHeader({ className, ...props }: React.ComponentProps<"thead">) {
  return (
    <thead
      data-slot="table-header"
      // beacon: on the panel's own surface, so a sticky header covers the rows scrolling beneath.
      className={cn(
        "bg-background [&_tr]:border-b [&_tr]:border-border-subtle [&_tr]:hover:bg-transparent",
        className,
      )}
      {...props}
    />
  )
}

function TableBody({ className, ...props }: React.ComponentProps<"tbody">) {
  return (
    <tbody
      data-slot="table-body"
      className={cn("[&_tr:last-child]:border-0", className)}
      {...props}
    />
  )
}

function TableFooter({ className, ...props }: React.ComponentProps<"tfoot">) {
  return (
    <tfoot
      data-slot="table-footer"
      // beacon: a sunken well.
      className={cn(
        "border-t border-border-subtle bg-surface-sunken font-medium [&>tr]:last:border-b-0",
        className,
      )}
      {...props}
    />
  )
}

function TableRow({ className, ...props }: React.ComponentProps<"tr">) {
  return (
    <tr
      data-slot="table-row"
      className={cn(
        // beacon: subtle row dividers, the accent fill on hover, the selected fill when chosen; a
        // named group so a row's actions can be revealed on hover.
        "group/row border-b border-border-subtle transition-colors duration-100 hover:bg-accent has-aria-expanded:bg-accent data-[state=selected]:bg-selected",
        className,
      )}
      {...props}
    />
  )
}

function TableHead({ className, ...props }: React.ComponentProps<"th">) {
  return (
    <th
      data-slot="table-head"
      className={cn(
        // beacon: 32px, 12px medium in the secondary tier; `data-align=end` right-justifies a
        // numeric column's heading.
        "h-8 px-3 text-left align-middle text-xs font-medium whitespace-nowrap text-muted-foreground data-[align=end]:text-right [&:has([role=checkbox])]:pr-0",
        className,
      )}
      {...props}
    />
  )
}

function TableCell({ className, ...props }: React.ComponentProps<"td">) {
  return (
    <td
      data-slot="table-cell"
      // beacon: 36px rows; `data-align=end` right-aligns a numeric column in tabular figures.
      className={cn(
        "h-9 px-3 align-middle whitespace-nowrap data-[align=end]:text-right data-[align=end]:tabular-nums [&:has([role=checkbox])]:pr-0",
        className,
      )}
      {...props}
    />
  )
}

function TableCaption({ className, ...props }: React.ComponentProps<"caption">) {
  return (
    <caption
      data-slot="table-caption"
      // beacon: tertiary meta text.
      className={cn("mt-4 text-xs text-subtle-foreground", className)}
      {...props}
    />
  )
}

export { Table, TableHeader, TableBody, TableFooter, TableHead, TableRow, TableCell, TableCaption }
